import { useState, useEffect } from 'react';

/**
 * Hook personalizado para gestionar la lógica de descuentos jerárquicos
 * @param {Object} params Parámetros necesarios para calcular descuentos
 * @returns {Object} Funciones y estados para manejar descuentos
 */
const useDescuentosJerarquicos = ({
  origen = '',
  medicoSeleccionado = '',
  clienteSeleccionado = null,
  medioPago = '',
  fechaCotizacion = '',
  analisisAgregados = [],
  setAnalisisAgregados = () => {},
  setDescuentoGlobal = () => {},
  setDescuentoGlobalRemitido = () => {},
  setDescuentoAsegurado = () => {},
  calcularTotales = () => {},
  valorCopago = 0,
  server = '',
  perfilSeleccionado = null,
  sucursalSeleccionada = null // Nuevo: para obtener descuentos específicos por sucursal
}) => {
  const [descuentosFuente, setDescuentosFuente] = useState({
    descuentoNormal: 0,
    descuentoRemitido: 0,
    fuente: 'ninguno',
    aplicaSobre: 'total'
  });
  const [logDescuento, setLogDescuento] = useState([]);

  /**
   * Identifica el tipo de origen
   * @returns {string} Tipo de origen ('sin_origen', 'empresa', 'aseguradora', 'normal')
   */
  const identificarTipoOrigen = () => {
    if (!origen) return 'sin_origen';
    if (origen.empresa === true) return 'empresa';
    if (origen.aseguradora === true) return 'aseguradora';
    return 'normal';
  };

  /**
   * Verifica si el cliente pertenece a la tercera o cuarta edad y asigna el descuento correspondiente
   * @returns {Object} Estado de preferencia por edad con el porcentaje de descuento
   */
  const esClienteTerceraOCuartaEdad = () => {
    // Si no hay cliente seleccionado o no tiene fecha de nacimiento, no hay descuento
    if (!clienteSeleccionado?.fechaNacimiento) {
      console.log('ℹ️ No hay cliente seleccionado o no tiene fecha de nacimiento');
      return { es: false, tipo: null, porcentaje: 0 };
    }
    
    console.log(`🔍 Verificando edad preferencial para cliente con fecha de nacimiento: ${clienteSeleccionado.fechaNacimiento}`);
    
    // Verificar primero cuarta edad (mayor prioridad)
    const esCuarta = esCuartaEdad(clienteSeleccionado.fechaNacimiento);
    if (esCuarta) {
      console.log('✅ Cliente califica para descuento de cuarta edad (40%)');
      return { 
        es: true, 
        tipo: 'cuarta', 
        porcentaje: 40, 
        edad: calcularEdad(clienteSeleccionado.fechaNacimiento)
      };
    }
    
    // Si no es de cuarta edad, verificar tercera edad
    const esTercera = esTerceraEdad(clienteSeleccionado.fechaNacimiento);
    if (esTercera) {
      console.log('✅ Cliente califica para descuento de tercera edad (30%)');
      return { 
        es: true, 
        tipo: 'tercera', 
        porcentaje: 30, 
        edad: calcularEdad(clienteSeleccionado.fechaNacimiento)
      };
    }
    
    // No califica para descuentos por edad
    console.log('ℹ️ Cliente no califica para descuentos por edad preferencial');
    return { 
      es: false, 
      tipo: null, 
      porcentaje: 0, 
      edad: calcularEdad(clienteSeleccionado.fechaNacimiento)
    };
  };

  /**
   * Calcula la edad exacta a partir de una fecha de nacimiento
   * @param {string|Date} fechaNacimiento Fecha de nacimiento
   * @returns {number} Edad calculada
   */
  const calcularEdad = (fechaNacimiento) => {
    if (!fechaNacimiento) return 0;
    
    // Convertir a objeto Date si es string
    const fechaNac = fechaNacimiento instanceof Date ? 
                    fechaNacimiento : 
                    new Date(fechaNacimiento);
    
    // Verificar si es una fecha válida
    if (isNaN(fechaNac.getTime())) {
      console.error(`❌ Fecha de nacimiento inválida: ${fechaNacimiento}`);
      return 0;
    }
    
    const hoy = new Date();
    let edad = hoy.getFullYear() - fechaNac.getFullYear();
    
    // Ajustar si todavía no ha cumplido años este año
    const m = hoy.getMonth() - fechaNac.getMonth();
    if (m < 0 || (m === 0 && hoy.getDate() < fechaNac.getDate())) {
      edad--;
    }
    
    return edad;
  };

  /**
   * Determina si una fecha corresponde a una persona de tercera edad (60+)
   * @param {string} fechaNacimiento Fecha de nacimiento
   * @returns {boolean} True si es de tercera edad
   */
  const esTerceraEdad = (fechaNacimiento) => {
    const edad = calcularEdad(fechaNacimiento);
    const esTercera = edad >= 60 && edad < 80;
    
    if (esTercera) {
      console.log(`✅ Cliente de tercera edad detectado: ${edad} años`);
    }
    
    return esTercera;
  };

  /**
   * Determina si una fecha corresponde a una persona de cuarta edad (80+)
   * @param {string} fechaNacimiento Fecha de nacimiento
   * @returns {boolean} True si es de cuarta edad
   */
  const esCuartaEdad = (fechaNacimiento) => {
    const edad = calcularEdad(fechaNacimiento);
    const esCuarta = edad >= 80;
    
    if (esCuarta) {
      console.log(`✅ Cliente de cuarta edad detectado: ${edad} años`);
    }
    
    return esCuarta;
  };

  /**
   * Obtiene descuentos por origen (normal, empresa, aseguradora)
   * @param {number} idOrigen ID del origen
   * @returns {Object} Descuentos normal y remitido
   */
  const obtenerDescuentoPorOrigen = async (idOrigen) => {
    try {
      const response = await fetch(`${server}/mg_origen_empresa_descuento_por_uso/byidorigen/${idOrigen}`);
      const data = await response.json();
      const records = data.$values ? data.$values : (Array.isArray(data) ? data : []);
      const registro = records.find(item => item.id_Clasificacion_Producto === 2);
      
      if (registro) {
        return {
          descuento: registro.descuento || 0,
          descuento_remitido: registro.descuento_remitido || 0
        };
      } else {
        return { descuento: 0, descuento_remitido: 0 };
      }
    } catch (error) {
      console.error('Error al obtener descuento por origen:', error);
      return { descuento: 0, descuento_remitido: 0 };
    }
  };

  /**
   * Obtiene descuento por tarjeta/medio de pago
   * @param {number} idMedioPago ID del medio de pago
   * @returns {Object} Porcentaje de descuento e ID de promoción
   */
  /*** Obtiene el descuento asociado a un método de pago específico
   * @param {number|object} idMedioPago ID del método de pago o objeto con id
   * @returns {Promise<Object>} Información del descuento (porcentaje, idPromocion)
   */
  const obtenerDescuentoPorTarjeta = async (idMedioPago) => {
    try {
      // Si recibimos un objeto, extraemos el ID
      const medioPagoId = typeof idMedioPago === 'object' ? idMedioPago.id : idMedioPago;
      
      if (!medioPagoId) {
        console.log('⚠️ No se proporcionó ID de medio de pago para consultar descuento');
        return {};
      }
      
      if (!sucursalSeleccionada?.idSucursal) {
        console.log('⚠️ No se encontró el ID de sucursal para consultar descuento por método de pago');
        return {};
      }
      
      console.log(`🔍 Consultando descuento para método de pago ID: ${medioPagoId}, Sucursal: ${sucursalSeleccionada.idSucursal}`);
      
      // Usar el endpoint específico que incluye el idSucursal
      const endpoint = `${server}/PrDetallesDeDescuentosXTarjetasBancarias/byidmetodopago/${medioPagoId}/${sucursalSeleccionada.idSucursal}`;
      console.log(`🔌 Endpoint: ${endpoint}`);
      
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          console.log('⚠️ No se encontró descuento para este método de pago en la sucursal seleccionada');
          // Si no se encuentra, intentamos con la lógica antigua por compatibilidad
          return await obtenerDescuentoPorTarjetaLegacy(medioPagoId);
        }
        throw new Error(`Error obteniendo descuento de medio de pago. Código: ${response.status}`);
      }
      
      const data = await response.json();
      console.log(`✅ Datos de descuento obtenidos:`, data);
      
      // Verificamos que data tenga las propiedades que necesitamos
      if (data && data.porcentaje !== undefined) {
        return {
          idPromocion: data.idPromocion,
          porcentaje: data.porcentaje,
          descuentoRemitido: data.descuentoRemitido || 0 // Nueva propiedad
        };
      }
      
      // Si no hay datos válidos, intentamos con la lógica anterior
      console.log('⚠️ Los datos recibidos no tienen el formato esperado, intentando con lógica legacy');
      return await obtenerDescuentoPorTarjetaLegacy(medioPagoId);
    } catch (error) {
      console.error('Error al obtener el descuento por medio de pago:', error);
      console.log('⚠️ Intentando con lógica legacy debido al error');
      return await obtenerDescuentoPorTarjetaLegacy(medioPagoId);
    }
  };
  
  /**
   * Método legacy para obtener descuento por tarjeta sin considerar sucursal
   * @param {number} medioPagoId ID del método de pago
   * @returns {Promise<Object>} Información del descuento
   */
  const obtenerDescuentoPorTarjetaLegacy = async (medioPagoId) => {
    try {
      console.log(`🔍 Usando método legacy para consultar descuento para método de pago ID: ${medioPagoId}`);
      
      const response = await fetch(`${server}/PrDetallesDeDescuentosXTarjetasBancarias/byidmetodopago/${medioPagoId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          console.log('⚠️ No se encontró descuento para este método de pago (legacy)');
          return {};
        }
        throw new Error('Error obteniendo descuento legacy de medio de pago');
      }
      
      const data = await response.json();
      console.log(`✅ Datos de descuento legacy obtenidos:`, data);
      
      // Verificamos que data tenga las propiedades que necesitamos
      if (data && data.porcentaje !== undefined) {
        return {
          idPromocion: data.idPromocion,
          porcentaje: data.porcentaje,
          descuentoRemitido: data.descuentoRemitido || 0
        };
      }
      
      return {};
    } catch (error) {
      console.error('Error al obtener el descuento legacy por medio de pago:', error);
      return {};
    }
  };
 

  /**
   * Obtiene todos los descuentos disponibles según el contexto actual
   * @returns {Object} Todos los descuentos disponibles
   */
  const obtenerTodosLosDescuentos = async () => {
    console.log('🔍 Obteniendo todos los descuentos disponibles para el contexto actual');
    
    const descuentos = {
      // Descuentos por origen
      origen: 0,
      origenRemitido: 0,
      
      // Descuentos por edad
      edad: esClienteTerceraOCuartaEdad(),
      
      // Descuento por médico
      medico: medicoSeleccionado?.porcentajeDescuento || 0,
      
      // Descuento por método de pago
      metodoPago: 0,
      
      // Nota: El descuento por perfil no se incluye aquí globalmente
      // ya que solo se aplica a los análisis específicos de ese perfil
      // y será manejado en recalcularTodosLosAnalisis
      
      // Descuentos específicos (se calculan por análisis/perfil)
      perfilEspecifico: 0,
      analisisEspecifico: 0
    };

    // Obtener descuento por origen si existe
    if (origen) {
      console.log(`🔍 Consultando descuento para origen: ${origen.descripcionOrigen} (ID: ${origen.idOrigen})`);
      const { descuento, descuento_remitido } = await obtenerDescuentoPorOrigen(origen.idOrigen);
      descuentos.origen = descuento || 0;
      descuentos.origenRemitido = descuento_remitido || 0;
      console.log(`✅ Descuento por origen: ${descuentos.origen}%, Remitido: ${descuentos.origenRemitido}%`);
    }

    // Obtener descuento por método de pago si existe
    if (medioPago) {
      console.log(`🔍 Consultando descuento para método de pago: ${medioPago.descripcion} (ID: ${medioPago.id})`);
      
      // Usar el nuevo método que incluye la sucursal seleccionada
      const { porcentaje = 0 } = await obtenerDescuentoPorTarjeta(medioPago.id) || {};
      descuentos.metodoPago = porcentaje;
      
      console.log(`✅ Descuento por método de pago: ${descuentos.metodoPago}%`);
    }

    return descuentos;
  };

  /**
   * Función principal que calcula los descuentos según jerarquía
   * 
   * Esta función se debe llamar cada vez que cambie:
   * - El cliente seleccionado
   * - El origen
   * - El médico
   * - El método de pago
   * - El perfil seleccionado
   */
  const calcularDescuentoJerarquico = async () => {
    console.log('🔍 Iniciando cálculo jerárquico de descuentos');
    
    // 1) Identificar el contexto
    const tipoOrigen = identificarTipoOrigen();
    const clientePreferencial = esClienteTerceraOCuartaEdad();
    
    // Registrar el contexto para fines de debugging y seguimiento
    console.log(`📋 CONTEXTO ACTUAL:`);
    console.log(`   - Tipo de origen: ${tipoOrigen}`);
    console.log(`   - Cliente seleccionado: ${clienteSeleccionado ? clienteSeleccionado.nombre || clienteSeleccionado.nombreCompleto : 'No seleccionado'}`);
    console.log(`   - Es cliente preferencial: ${clientePreferencial.es ? `Sí (${clientePreferencial.tipo} edad, ${clientePreferencial.edad} años)` : 'No'}`);
    console.log(`   - Médico: ${medicoSeleccionado ? medicoSeleccionado.nombre || medicoSeleccionado.descripcion : 'No seleccionado'}`);
    console.log(`   - Método de pago: ${medioPago ? medioPago.descripcion : 'No seleccionado'}`);
    console.log(`   - Perfil seleccionado: ${perfilSeleccionado ? perfilSeleccionado.descripcionPerfil : 'No seleccionado'}`);
    
    // 2) Obtener todos los descuentos disponibles
    const descuentosDisponibles = await obtenerTodosLosDescuentos();
    
    // 3) Aplicar jerarquía según escenario
    const configuracionFinal = aplicarJerarquiaDescuentos(tipoOrigen, clientePreferencial, descuentosDisponibles);
    
    // Verificar si hay cambios en la configuración de descuentos
    const hayNuevoDescuento = descuentosFuente.descuentoNormal !== configuracionFinal.descuentoNormal ||
                              descuentosFuente.fuente !== configuracionFinal.fuente;
    
    if (hayNuevoDescuento) {
      console.log('⚠️ CAMBIO EN DESCUENTO GLOBAL DETECTADO:');
      console.log(`   - Anterior: ${descuentosFuente.descuentoNormal}% (${descuentosFuente.fuente})`);
      console.log(`   - Nuevo: ${configuracionFinal.descuentoNormal}% (${configuracionFinal.fuente})`);
    } else {
      console.log('✓ No hay cambios en la configuración global de descuentos');
    }
    
    // 4) Establecer descuentos globales
    setDescuentoGlobal(configuracionFinal.descuentoNormal);
    setDescuentoGlobalRemitido(configuracionFinal.descuentoRemitido);
    setDescuentosFuente(configuracionFinal);
    
    if (configuracionFinal.aplicaDescuentoAsegurado) {
      setDescuentoAsegurado(configuracionFinal.descuentoAsegurado);
    }
    
    // Registrar log para debugging
    setLogDescuento([
      ...logDescuento,
      {
        timestamp: new Date().toISOString(),
        tipoOrigen,
        clientePreferencial,
        descuentosDisponibles,
        configuracionFinal
      }
    ]);
    
    console.log('✅ Configuración final aplicada:', configuracionFinal);
    
    // 5) Recalcular análisis agregados
    const nuevosAnalisis = await recalcularTodosLosAnalisis();
    setAnalisisAgregados(nuevosAnalisis);
    calcularTotales(nuevosAnalisis);
  };

  /**
   * Aplica la jerarquía de descuentos según el escenario
   * @param {string} tipoOrigen Tipo de origen
   * @param {Object} clientePreferencial Info del cliente preferencial
   * @param {Object} descuentos Descuentos disponibles
   * @returns {Object} Configuración final de descuentos
   */
  const aplicarJerarquiaDescuentos = (tipoOrigen, clientePreferencial, descuentos) => {
    let configuracion = {
      descuentoNormal: 0,
      descuentoRemitido: 0,
      aplicaDescuentoAsegurado: false,
      descuentoAsegurado: 0,
      fuente: 'ninguno'
    };

    console.log(`📊 JERARQUÍA DE DESCUENTOS:`);
    console.log(`   Tipo de origen: ${tipoOrigen}`);
    console.log(`   Cliente tercera/cuarta edad: ${clientePreferencial.es ? 'Sí' : 'No'}`);
    if (clientePreferencial.es) {
      console.log(`   Tipo de edad preferencial: ${clientePreferencial.tipo}, Porcentaje: ${clientePreferencial.porcentaje}%`);
    }
    
    console.log(`   Descuento por médico: ${descuentos.medico}%`);
    console.log(`   Descuento por método de pago: ${descuentos.metodoPago}%`);
    console.log(`   Descuento por origen: ${descuentos.origen}%, Remitido: ${descuentos.origenRemitido}%`);

    // Aplicar la jerarquía según el tipo de origen
    switch (tipoOrigen) {
      case 'sin_origen':
        return aplicarJerarquiaSinOrigen(clientePreferencial, descuentos);
        
      case 'empresa':
        return aplicarJerarquiaEmpresa(clientePreferencial, descuentos);
        
      case 'aseguradora':
        return aplicarJerarquiaAseguradora(clientePreferencial, descuentos);
        
      case 'normal':
      default:
        return aplicarJerarquiaNormal(clientePreferencial, descuentos);
    }
  };

  /**
   * Aplica jerarquía cuando no hay origen seleccionado
   * Regla de negocio: Se aplica el mayor descuento entre médico, método de pago y edad.
   * 
   * @param {Object} clientePreferencial Info del cliente preferencial
   * @param {Object} descuentos Descuentos disponibles
   * @returns {Object} Configuración de descuentos
   */
  const aplicarJerarquiaSinOrigen = (clientePreferencial, descuentos) => {
    console.log('🔄 SIN ORIGEN:');
    console.log('   Cuando no hay origen seleccionado, se aplica el mayor descuento entre médico, método de pago y edad');
    
    // Validar valores y usar 0 para valores nulos o indefinidos
    const descuentoMedico = (descuentos.medico !== null && descuentos.medico !== undefined) ? descuentos.medico : 0;
    const descuentoMetodoPago = (descuentos.metodoPago !== null && descuentos.metodoPago !== undefined) ? descuentos.metodoPago : 0;
    const descuentoEdad = clientePreferencial.es ? (clientePreferencial.porcentaje || 0) : 0;
    
    console.log(`   Descuento por médico: ${descuentoMedico}%`);
    console.log(`   Descuento por método pago: ${descuentoMetodoPago}%`);
    console.log(`   Descuento por edad: ${descuentoEdad}%`);
    
    // Arreglo con descuentos disponibles (valores validados)
    const otrosDescuentos = [descuentoMedico, descuentoMetodoPago, descuentoEdad];
    
    // Obtener el mayor descuento
    const mayorDescuento = Math.max(...otrosDescuentos);
    console.log(`   Mayor descuento encontrado: ${mayorDescuento}%`);
    
    // Determinar la fuente del descuento mayor
    let fuente = 'ninguno';
    
    if (mayorDescuento === descuentoMedico && descuentoMedico > 0) {
      fuente = 'medico';
      console.log('✅ Descuento seleccionado: Por médico');
    } else if (mayorDescuento === descuentoMetodoPago && descuentoMetodoPago > 0) {
      fuente = 'metodoPago';
      console.log('✅ Descuento seleccionado: Por método de pago');
    } else if (mayorDescuento === descuentoEdad && descuentoEdad > 0) {
      fuente = 'edad';
      console.log(`✅ Descuento seleccionado: Por ${clientePreferencial.tipo} edad`);
    } else {
      console.log('⚠️ No se encontraron descuentos aplicables');
    }
    
    return {
      descuentoNormal: mayorDescuento,
      descuentoRemitido: 0, // Sin origen = no hay descuento remitido
      fuente: fuente,
      aplicaSobre: 'total'
    };
  };

  /**
   * Aplica jerarquía cuando el origen es empresa
   * Regla de negocio: Se aplica el mayor entre el descuento de empresa y el descuento por edad.
   * Los descuentos por médico y método de pago no aplican en este caso.
   * 
   * @param {Object} clientePreferencial Info del cliente preferencial
   * @param {Object} descuentos Descuentos disponibles
   * @returns {Object} Configuración de descuentos
   */
  const aplicarJerarquiaEmpresa = (clientePreferencial, descuentos) => {
    // REGLA DE EMPRESA: 
    // 1. Por defecto aplicamos el descuento de empresa a todos los items
    // 2. Solo el descuento por edad puede superar al descuento de empresa (si es mayor)
    
    // Validar valores
    const descuentoEmpresa = descuentos.origen || 0;
    const descuentoEdad = clientePreferencial.es ? clientePreferencial.porcentaje || 0 : 0;
    const descuentoRemitido = descuentos.origenRemitido || 0;
    
    console.log('🔄 ORIGEN EMPRESA:');
    console.log(`   Descuento por empresa: ${descuentoEmpresa}%`);
    console.log(`   Descuento remitido: ${descuentoRemitido}%`);
    console.log(`   Descuento por edad: ${descuentoEdad}%`);
    console.log(`   Los descuentos por médico y método de pago NO aplican en caso de origen empresa`);
    
    let descuentoFinal = descuentoEmpresa;
    let fuente = 'empresa';
    
    // Verificar si el descuento por edad es mayor que el de empresa
    if (descuentoEdad > descuentoEmpresa) {
      descuentoFinal = descuentoEdad;
      fuente = 'edad_sobre_empresa';
      console.log(`✅ El descuento por ${clientePreferencial.tipo} edad (${descuentoEdad}%) supera al de empresa (${descuentoEmpresa}%)`);
    } else {
      console.log(`✅ El descuento de empresa (${descuentoEmpresa}%) es mayor o igual al descuento por edad (${descuentoEdad}%)`);
    }
    
    return {
      descuentoNormal: descuentoFinal,
      descuentoRemitido: descuentoRemitido,
      fuente: fuente,
      aplicaSobre: 'total'
    };
  };

  /**
   * Aplica jerarquía cuando el origen es aseguradora
   * Regla de negocio: 
   * 1. Se aplica el descuento de aseguradora a todos los análisis
   * 2. Si el cliente es de tercera/cuarta edad, su descuento se aplica sobre el COPAGO
   * 3. Los descuentos por médico y método de pago no aplican en este caso
   * 
   * @param {Object} clientePreferencial Info del cliente preferencial
   * @param {Object} descuentos Descuentos disponibles
   * @returns {Object} Configuración de descuentos
   */
  const aplicarJerarquiaAseguradora = (clientePreferencial, descuentos) => {
    // Validar valores
    const descuentoAseguradora = descuentos.origen || 0;
    const descuentoRemitido = descuentos.origenRemitido || 0;
    const descuentoEdad = clientePreferencial.es ? (clientePreferencial.porcentaje || 0) : 0;
    
    console.log('🔄 ORIGEN ASEGURADORA:');
    console.log(`   Descuento por aseguradora: ${descuentoAseguradora}%`);
    console.log(`   Descuento remitido: ${descuentoRemitido}%`);
    console.log(`   Los descuentos por médico y método de pago NO aplican en caso de origen aseguradora`);
    
    // Configuración inicial con descuento de aseguradora
    let configuracion = {
      descuentoNormal: descuentoAseguradora,
      descuentoRemitido: descuentoRemitido,
      fuente: 'aseguradora',
      aplicaDescuentoAsegurado: false,
      descuentoAsegurado: 0,
      aplicaSobre: 'total'
    };
    
    // Caso especial: Descuento por edad aplica sobre el copago, no sobre el precio total
    if (clientePreferencial.es && descuentoEdad > 0) {
      console.log(`✅ Cliente es de ${clientePreferencial.tipo} edad con ${descuentoEdad}% de descuento`);
      console.log('✅ En caso de aseguradora, el descuento por edad se aplica sobre el COPAGO restante');
      
      configuracion.aplicaDescuentoAsegurado = true;
      configuracion.descuentoAsegurado = descuentoEdad;
      configuracion.fuente = 'edad_sobre_copago';
      configuracion.aplicaSobre = 'copago';
    } else {
      console.log('✅ No hay descuento adicional por edad para aplicar sobre el copago');
    }
    
    return configuracion;
  };

  /**
   * Aplica jerarquía cuando el origen es normal
   * Regla de negocio: El origen debe prevalecer sobre médico, promociones y método de pago
   * @param {Object} clientePreferencial Info del cliente preferencial
   * @param {Object} descuentos Descuentos disponibles
   * @returns {Object} Configuración de descuentos
   */
  const aplicarJerarquiaNormal = (clientePreferencial, descuentos) => {
    console.log('🔄 ORIGEN NORMAL:');
    console.log(`   Evaluando descuentos globales disponibles:`);
    console.log(`   - Descuento por origen: ${descuentos.origen}%`);
    console.log(`   - Descuento por médico: ${descuentos.medico}%`);
    console.log(`   - Descuento por método pago: ${descuentos.metodoPago}%`);
    
    if (clientePreferencial.es) {
      console.log(`   - Descuento por ${clientePreferencial.tipo} edad: ${clientePreferencial.porcentaje}%`);
    } else {
      console.log(`   - Sin descuento por edad`);
    }
    
    // Validar valores
    const descuentoOrigen = (descuentos.origen !== null && descuentos.origen !== undefined) ? descuentos.origen : 0;
    const descuentoMedico = (descuentos.medico !== null && descuentos.medico !== undefined) ? descuentos.medico : 0;
    const descuentoMetodoPago = (descuentos.metodoPago !== null && descuentos.metodoPago !== undefined) ? descuentos.metodoPago : 0;
    const descuentoEdad = clientePreferencial.es ? (clientePreferencial.porcentaje || 0) : 0;
    const descuentoRemitido = descuentos.origenRemitido || 0;
    
    let descuentoFinal = 0;
    let fuente = 'ninguno';
    
    // REGLA ACTUALIZADA: 
    // 1. Primero verificamos si el cliente es preferencial (3ra/4ta edad)
    // 2. Luego verificamos si hay origen
    // 3. Si no hay preferencial ni origen, aplicamos el mayor entre médico y método pago
    
    // Verificar primero si es cliente preferencial
    if (clientePreferencial.es && descuentoEdad > descuentoOrigen) {
      descuentoFinal = descuentoEdad;
      fuente = 'edad';
      console.log(`✅ Descuento por ${clientePreferencial.tipo} edad (${descuentoEdad}%) mayor que origen (${descuentoOrigen}%)`);
    } 
    // Si hay origen, prevalece sobre médico y método pago
    else if (descuentoOrigen > 0) {
      descuentoFinal = descuentoOrigen;
      fuente = 'origen';
      console.log(`✅ Descuento por origen (${descuentoOrigen}%) prevalece sobre médico y método pago`);
    } 
    // Si no hay origen, verificar entre médico y método pago
    else {
      // Obtener el mayor entre médico y método pago
      const mayorOtrosDescuentos = Math.max(descuentoMedico, descuentoMetodoPago);
      
      // Si hay descuento por edad, comparamos con el mayor de los otros
      if (clientePreferencial.es && descuentoEdad > mayorOtrosDescuentos) {
        descuentoFinal = descuentoEdad;
        fuente = 'edad';
        console.log(`✅ Descuento por ${clientePreferencial.tipo} edad (${descuentoEdad}%) mayor que otros descuentos`);
      }
      // Si el mayor es médico
      else if (descuentoMedico >= descuentoMetodoPago && descuentoMedico > 0) {
        descuentoFinal = descuentoMedico;
        fuente = 'medico';
        console.log(`✅ Descuento por médico (${descuentoMedico}%) mayor o igual que método pago`);
      } 
      // Si el mayor es método de pago
      else if (descuentoMetodoPago > 0) {
        descuentoFinal = descuentoMetodoPago;
        fuente = 'metodoPago';
        console.log(`✅ Descuento por método pago (${descuentoMetodoPago}%) mayor que médico`);
      }
      // Si ninguno tiene descuento
      else {
        console.log(`⚠️ No hay descuentos aplicables en este contexto`);
      }
    }
    
    console.log(`✅ Descuento final seleccionado: ${descuentoFinal}% (${fuente})`);
    
    return {
      descuentoNormal: descuentoFinal,
      descuentoRemitido: descuentoRemitido,
      fuente: fuente,
      aplicaSobre: 'total'
    };
  };

  /**
   * Recalcula todos los análisis con los descuentos configurados
   * @returns {Array} Nueva lista de análisis con descuentos aplicados
   */
  /**
   * Recalcula todos los análisis con los descuentos configurados según las reglas de negocio:
   * 1. Análisis remitido (máxima prioridad)
   * 2. Descuento de perfil (solo para análisis del perfil seleccionado)
   * 3. Descuentos globales (edad, doctor, origen, método pago) - aplican a todos los análisis
   * 4. Descuentos específicos de análisis individual
   * 
   * También verifica y elimina descuentos cuando:
   * - El cliente ya no es de tercera/cuarta edad
   * - El análisis ya no forma parte del perfil seleccionado
   * - El origen, doctor o método de pago ha cambiado
   * 
   * @returns {Promise<Array>} Análisis recalculados con sus descuentos
   */
  const recalcularTodosLosAnalisis = async () => {
    // 🛡️ PROTECCIÓN: NO RECALCULAR SI TODOS LOS ANÁLISIS SON DEL BACKEND EN MODO EDIT
    const todosDelBackend = analisisAgregados.length > 0 && 
                           analisisAgregados.every(a => a.descuentoDelBackend === true);
    
    if (todosDelBackend) {
      console.log('🛡️ PROTECCIÓN useDescuentosJerarquicos: Todos los análisis son del backend - NO RECALCULAR');
      console.log('📦 Manteniendo descuentos originales del backend para:', analisisAgregados.length, 'análisis');
      return analisisAgregados; // Retornar sin cambios
    }
    
    // Debug información
    console.log('🔄 INICIANDO RECÁLCULO DE TODOS LOS ANÁLISIS');
    console.log('🔍 Total de análisis a recalcular:', analisisAgregados.length);
    console.log('🔍 Fuente del descuento global:', descuentosFuente.fuente);
    console.log('🔍 Porcentaje de descuento global normal:', descuentosFuente.descuentoNormal);
    console.log('🔍 Porcentaje de descuento global remitido:', descuentosFuente.descuentoRemitido);
    
    // Debug información del perfil
    console.log('🔍 Perfil seleccionado:', perfilSeleccionado);
    if (perfilSeleccionado) {
      console.log('🔍 ID del perfil:', perfilSeleccionado.idPerfil);
      console.log('🔍 Nombre del perfil:', perfilSeleccionado.descripcionPerfil);
      console.log('🔍 Porcentaje de descuento del perfil:', perfilSeleccionado.porcentaje);
    }
    
    // Obtener información de edad del cliente
    const infoClientePreferencial = esClienteTerceraOCuartaEdad();
    console.log('🔍 Cliente preferencial por edad:', infoClientePreferencial);

    // Procesar todos los análisis en paralelo
    return await Promise.all(
      analisisAgregados.map(async (analisis, index) => {
        // Variables para el cálculo de descuentos
        let porcentajeDescuento = 0;
        let idPromocionAplicada = null;
        let fuenteDescuento = 'ninguno';
        // Guardar el descuento anterior para comparar
        const descuentoAnterior = {
          porcentaje: analisis.porcentajeDescuento || 0,
          fuente: analisis.fuenteDescuento || 'ninguno'
        };

        // Debug información por análisis
        console.log(`\n🔄 Recalculando análisis [${index}]: ${analisis.analisis}`);
        console.log(`   - ID: ${analisis.idTipoAnalisis}`);
        console.log(`   - Precio: ${analisis.precio}`);
        console.log(`   - Cantidad: ${analisis.cantidad}`);
        console.log(`   - ID Perfil asociado: ${analisis.idPerfil || 'N/A'}`);
        console.log(`   - Es parte del perfil seleccionado: ${analisis.esDePerfil ? 'Sí' : 'No'}`);
        console.log(`   - Es remitido: ${analisis.remitido ? 'Sí' : 'No'}`);
        console.log(`   - Descuento anterior: ${descuentoAnterior.porcentaje}% (Fuente: ${descuentoAnterior.fuente})`);

        // ⚠️ APLICACIÓN DE LA JERARQUÍA DE DESCUENTOS ⚠️
        console.log(`\n🔍 EVALUANDO JERARQUÍA DE DESCUENTOS PARA: ${analisis.analisis}`);
        
        // Verificar si el análisis es remitido
        const esRemitido = analisis.remitido === true;
        console.log(`   - ¿Es análisis remitido?: ${esRemitido ? 'SÍ' : 'NO'}`);
        
        // Verificar si tiene descuento remitido disponible
        const tieneDescuentoRemitido = descuentosFuente.descuentoRemitido > 0;
        console.log(`   - ¿Tiene descuento remitido?: ${tieneDescuentoRemitido ? `SÍ (${descuentosFuente.descuentoRemitido}%)` : 'NO'}`);
        
        // Verificar si pertenece al perfil seleccionado
        const perteneceAlPerfil = perfilSeleccionado && 
                                perfilSeleccionado !== '' &&
                                (
                                  (analisis.idPerfil && analisis.idPerfil === perfilSeleccionado.idPerfil) || 
                                  analisis.esDePerfil
                                );
        console.log(`   - ¿Pertenece al perfil seleccionado?: ${perteneceAlPerfil ? 'SÍ' : 'NO'}`);
        
        // Verificar si el perfil tiene descuento válido
        const perfilTieneDescuento = perfilSeleccionado && 
                                    perfilSeleccionado.porcentaje !== undefined && 
                                    perfilSeleccionado.porcentaje !== null &&
                                    perfilSeleccionado.porcentaje > 0;
        
        if (perfilTieneDescuento) {
          console.log(`   - Descuento disponible por perfil: ${perfilSeleccionado.porcentaje}%`);
        }

        // Verificar si hay que quitar algún descuento previo
        if (descuentoAnterior.porcentaje > 0) {
          // Validar si tenía un descuento de tercera/cuarta edad y ya no lo tiene
          if (descuentoAnterior.fuente === 'edad' && (!infoClientePreferencial.es || descuentosFuente.fuente !== 'edad')) {
            console.log(`⚠️ QUITANDO descuento por edad: El cliente ya no califica para descuento por edad`);
          }
          // Validar si tenía un descuento de perfil pero el análisis ya no pertenece al perfil
          else if (descuentoAnterior.fuente === 'perfil' && (!perteneceAlPerfil || !perfilTieneDescuento)) {
            console.log(`⚠️ QUITANDO descuento de perfil: El análisis ya no pertenece al perfil seleccionado`);
          }
          // Validar si tenía un descuento de origen pero el origen cambió
          else if (descuentoAnterior.fuente === 'origen' && descuentosFuente.fuente !== 'origen') {
            console.log(`⚠️ QUITANDO descuento de origen: El origen ha cambiado o ya no aplica`);
          }
          // Validar si tenía un descuento de médico pero el médico cambió
          else if (descuentoAnterior.fuente === 'medico' && descuentosFuente.fuente !== 'medico') {
            console.log(`⚠️ QUITANDO descuento de médico: El médico ha cambiado o ya no aplica`);
          }
          // Validar si tenía un descuento de método de pago pero este cambió
          else if (descuentoAnterior.fuente === 'metodoPago' && descuentosFuente.fuente !== 'metodoPago') {
            console.log(`⚠️ QUITANDO descuento de método de pago: El método ha cambiado o ya no aplica`);
          }
          // Validar si tenía un descuento de remitido pero ya no es remitido
          else if (descuentoAnterior.fuente === 'remitido' && (!esRemitido || !tieneDescuentoRemitido)) {
            console.log(`⚠️ QUITANDO descuento de remitido: El análisis ya no es remitido`);
          }
        }
        
        // 1️⃣ PRIORIDAD MÁXIMA: Análisis remitido (SIEMPRE prevalece, incluso sobre edad)
        if (esRemitido && tieneDescuentoRemitido) {
          porcentajeDescuento = descuentosFuente.descuentoRemitido;
          fuenteDescuento = 'remitido';
          console.log(`📍 PRIORIDAD 1: Aplicando descuento remitido: ${porcentajeDescuento}% a ${analisis.analisis}`);
        } 
        // 2️⃣ SEGUNDA PRIORIDAD: Descuento de perfil específico
        // Solo aplica a los análisis que pertenecen al perfil seleccionado
        else if (perteneceAlPerfil && perfilTieneDescuento) {
          porcentajeDescuento = perfilSeleccionado.porcentaje;
          idPromocionAplicada = perfilSeleccionado.idPromocion || null;
          fuenteDescuento = 'perfil';
          const nombrePerfil = perfilSeleccionado.descripcionPerfil || 'Sin nombre';
          console.log(`📍 PRIORIDAD 2: Aplicando descuento específico de perfil: ${porcentajeDescuento}% a ${analisis.analisis} (parte del perfil ${nombrePerfil})`);
        }
        // 3️⃣ TERCERA PRIORIDAD: Descuentos globales
        // Los descuentos globales se aplican a todos los análisis que no tengan descuentos de mayor prioridad
        else if (descuentosFuente.descuentoNormal > 0) {
          porcentajeDescuento = descuentosFuente.descuentoNormal;
          fuenteDescuento = descuentosFuente.fuente;
          
          // Información más detallada sobre el tipo de descuento global
          let tipoDescuento;
          switch(descuentosFuente.fuente) {
            case 'edad':
              tipoDescuento = "tercera/cuarta edad";
              break;
            case 'edad_sobre_empresa':
              tipoDescuento = "tercera/cuarta edad sobre empresa";
              break;
            case 'edad_sobre_copago':
              tipoDescuento = "tercera/cuarta edad sobre copago";
              break;
            case 'medico':
              tipoDescuento = "doctor";
              break;
            case 'metodoPago':
              tipoDescuento = "método de pago";
              break;
            case 'empresa':
              tipoDescuento = "empresa";
              break;
            case 'aseguradora':
              tipoDescuento = "aseguradora";
              break;
            case 'origen':
              tipoDescuento = "origen";
              break;
            default:
              tipoDescuento = descuentosFuente.fuente;
          }
          
          console.log(`📍 PRIORIDAD 3: Aplicando descuento global por ${tipoDescuento}: ${porcentajeDescuento}% a ${analisis.analisis}`);
        }
        // 4️⃣ CUARTA PRIORIDAD: Descuentos específicos por análisis individual
        else {
          try {
            // Intentar obtener un descuento específico para este análisis
            console.log(`🔍 Consultando descuentos específicos para análisis ID: ${analisis.idTipoAnalisis}`);
            const descuentoAnalisis = await obtenerDescuentoPorAnalisis(analisis.idTipoAnalisis);
            
            if (descuentoAnalisis && descuentoAnalisis.porcentaje > 0) {
              porcentajeDescuento = descuentoAnalisis.porcentaje;
              idPromocionAplicada = descuentoAnalisis.idPromocion;
              fuenteDescuento = 'analisis_especifico';
              console.log(`📍 PRIORIDAD 4a: Aplicando descuento específico por análisis: ${porcentajeDescuento}% a ${analisis.analisis}`);
            } 
            // Intentar con el perfil asociado si existe
            else if (analisis.idPerfil && (!perfilSeleccionado || analisis.idPerfil !== perfilSeleccionado.idPerfil)) {
              console.log(`🔍 Consultando descuentos para el perfil asociado ID: ${analisis.idPerfil}`);
              const descuentoPerfil = await obtenerDescuentoPorPerfil(analisis.idPerfil, null);
              if (descuentoPerfil && descuentoPerfil.porcentaje > 0) {
                porcentajeDescuento = descuentoPerfil.porcentaje;
                idPromocionAplicada = descuentoPerfil.idPromocion;
                fuenteDescuento = 'perfil_asociado';
                console.log(`📍 PRIORIDAD 4b: Aplicando descuento por perfil asociado: ${porcentajeDescuento}% a ${analisis.analisis}`);
              } else {
                console.log(`ℹ️ No se encontraron descuentos específicos para este análisis ni su perfil asociado`);
              }
            } else {
              console.log(`ℹ️ No se encontraron descuentos específicos para este análisis`);
            }
          } catch (error) {
            console.error('❌ Error al obtener descuentos específicos:', error);
            porcentajeDescuento = 0;
          }
        }

        // Verificar si hay un cambio de descuento y loggearlo
        if (descuentoAnterior.porcentaje !== porcentajeDescuento || descuentoAnterior.fuente !== fuenteDescuento) {
          if (porcentajeDescuento > 0) {
            console.log(`🔄 Cambio de descuento: ${descuentoAnterior.porcentaje}% (${descuentoAnterior.fuente}) -> ${porcentajeDescuento}% (${fuenteDescuento})`);
          } else if (descuentoAnterior.porcentaje > 0) {
            console.log(`🔄 Se eliminó el descuento anterior de ${descuentoAnterior.porcentaje}% (${descuentoAnterior.fuente})`);
          }
        } else if (porcentajeDescuento > 0) {
          console.log(`✅ Se mantiene el mismo descuento: ${porcentajeDescuento}% (${fuenteDescuento})`);
        }

        // Calcular valores finales con el porcentaje de descuento determinado
        console.log(`✅ Descuento final para ${analisis.analisis}: ${porcentajeDescuento}% (Fuente: ${fuenteDescuento})`);
        
        // Validar que los valores numéricos sean correctos
        const precio = Number(analisis.precio) || 0;
        const cantidad = Number(analisis.cantidad) || 1;
        
        // Calcular el monto de descuento
        const descuentoMonto = (precio * porcentajeDescuento) / 100;
        
        // Calcular precio con descuento
        const precioConDescuento = precio - descuentoMonto;
        
        // Calcular total (precio con descuento * cantidad)
        const total = precioConDescuento * cantidad;
        
        // Debug del cálculo final
        console.log(`   - Precio original: $${precio}`);
        console.log(`   - Descuento (${porcentajeDescuento}%): $${descuentoMonto.toFixed(2)}`);
        console.log(`   - Precio con descuento: $${precioConDescuento.toFixed(2)}`);
        console.log(`   - Cantidad: ${cantidad}`);
        console.log(`   - Total: $${total.toFixed(2)}`);
        
        // Retornar el análisis con los nuevos valores calculados
        return {
          ...analisis,
          descuento: descuentoMonto.toFixed(2),
          porcentajeDescuento: porcentajeDescuento,
          fuenteDescuento: fuenteDescuento,
          precioConDescuento: precioConDescuento.toFixed(2),
          total: total.toFixed(2),
          idPromocion: idPromocionAplicada
        };
      })
    );
  };

  /**
   * Función auxiliar para obtener descuento por análisis
   * Esta función debe estar implementada en el componente principal
   * o proporcionada como prop
   */
  /**
   * Obtiene el descuento específico para un análisis determinado
   * @param {number|Object} idTipoAnalisis ID del tipo de análisis o objeto con idTipoAnalisis
   * @returns {Promise<Object>} Información del descuento (porcentaje, idPromocion)
   */
  const obtenerDescuentoPorAnalisis = async (idTipoAnalisis) => {
    try {
      // Si recibimos un objeto, extraemos el ID
      const analisisId = typeof idTipoAnalisis === 'object' ? idTipoAnalisis.idTipoAnalisis : idTipoAnalisis;
      
      // Validar que tengamos un ID y una sucursal válida
      if (!analisisId) {
        console.log('⚠️ ID de análisis inválido o no proporcionado');
        return { porcentaje: 0 };
      }
      
      if (!sucursalSeleccionada?.idSucursal) {
        console.log('⚠️ ID de sucursal no disponible para consultar descuento por análisis');
        return { porcentaje: 0 };
      }
      
      console.log(`🔍 Consultando descuento específico para análisis ID: ${analisisId}, Sucursal: ${sucursalSeleccionada.idSucursal}`);
      
      const endpoint = `${server}/PrPromocionesDetallesDeDescuentoes/byidtipoAnalisis/${analisisId}/${sucursalSeleccionada.idSucursal}`;
      console.log(`🔌 Endpoint: ${endpoint}`);
      
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      
      // Manejo de respuesta
      if (!response.ok) {
        if (response.status === 404) {
          console.log('ℹ️ No se encontró descuento específico para este análisis');
          return { porcentaje: 0 };
        }
        throw new Error(`Error obteniendo descuento. Código: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('✅ Datos de descuento específico obtenidos:', data);
      
      // La API ahora devuelve un objeto directo
      if (data && data.porcentaje !== undefined) {
        console.log(`✅ Descuento específico encontrado: ${data.porcentaje}%`);
        return {
          idPromocion: data.idPromocion,
          porcentaje: data.porcentaje,
          descuentoRemitido: data.descuentoRemitido || 0
        };
      }
      
      // Si hay datos pero no tienen el formato esperado
      console.log('⚠️ Los datos obtenidos no tienen el formato esperado');
      return { porcentaje: 0 };
    } catch (error) {
      console.error('❌ Error al obtener el descuento por análisis:', error);
      return { porcentaje: 0 };
    }
  };

  /**
   * Función auxiliar para obtener descuento por perfil
   * Esta función debe estar implementada en el componente principal
   * o proporcionada como prop
   */
  const obtenerDescuentoPorPerfil = async (idPerfil, perfilObj = null) => {
    // Verificar que el perfil no sea nulo, vacío o inválido
    if (!idPerfil || idPerfil <= 0) {
      console.log('⚠️ ID de perfil inválido o no especificado:', idPerfil);
      return { porcentaje: 0, descuento_remitido: 0, idPromocion: null };
    }
    
    // Si recibimos un objeto perfil directamente, usar sus propiedades en lugar de hacer una llamada API
    if (perfilObj && 
        typeof perfilObj === 'object' && 
        perfilObj !== null &&
        perfilObj.porcentaje !== undefined && 
        perfilObj.porcentaje !== null) {
      console.log('✅ Usando porcentaje directamente del objeto perfil:', perfilObj.porcentaje);
      return { 
        porcentaje: perfilObj.porcentaje || 0, 
        descuento_remitido: perfilObj.descuento_remitido || 0,
        idPromocion: perfilObj.idPromocion || null 
      };
    }

    // Implementación original con llamadas API como fallback
    try {
      console.log('⚠️ Llamando a API para obtener descuento del perfil. Considera pasar el objeto perfil completo.');
      const fechaActual = new Date();
      // Usar la fecha actual para consultar promociones vigentes
      const fechaActualFormateada = fechaActual.toISOString().split('T')[0];

      const response = await fetch(`${server}/PrPromociones/byidtipopromocion/${idPerfil}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          search: '',
          desde: fechaActualFormateada,
          hasta: fechaActualFormateada
        })
      });
      
      const data = await response.json();
      const promociones = data.$values || [];

      // Filtrar promociones activas según la fecha de cotización
      const fechaCot = new Date(fechaCotizacion || fechaActualFormateada);
      const promocionesActivas = promociones.filter(promo => {
        // Verificar que las fechas existan antes de convertir
        if (!promo.fechaDesde || !promo.fechaHasta) return false;
        
        const fechaDesdePromo = new Date(promo.fechaDesde);
        const fechaHastaPromo = new Date(promo.fechaHasta);
        
        return fechaCot >= fechaDesdePromo && fechaCot <= fechaHastaPromo;
      });

      let maxDescuento = 0;
      let idPromocionAplicada = null;
      
      for (const promo of promocionesActivas) {
        const responseDetalles = await fetch(`${server}/PrDetallesDeDescuentosXPerfilDeAnalisis/byidpromocion/${promo.id}`);
        const dataDetalles = await responseDetalles.json();
        const detalles = dataDetalles.$values || [];

        // Buscar si el perfil seleccionado está en los detalles
        const detallePerfil = detalles.find(detalle => detalle.idPerfil === idPerfil);

        if (detallePerfil && detallePerfil.porcentaje > maxDescuento) {
          maxDescuento = detallePerfil.porcentaje;
          idPromocionAplicada = promo.id;
        }
      }

      return { porcentaje: maxDescuento, idPromocion: idPromocionAplicada };
    } catch (error) {
      console.error('Error al obtener el descuento por perfil:', error);
      return { porcentaje: 0, idPromocion: null };
    }
  };

  /**
   * Muestra un resumen de los descuentos actualmente aplicados
   * @returns {Object} Resumen de descuentos
   */
  const mostrarResumenDescuentos = () => {
    const resumen = {
      descuentoNormal: descuentosFuente.descuentoNormal,
      descuentoRemitido: descuentosFuente.descuentoRemitido,
      fuente: descuentosFuente.fuente,
      tipoOrigen: identificarTipoOrigen(),
      clientePreferencial: esClienteTerceraOCuartaEdad(),
      totalAnalisis: analisisAgregados.length
    };

    console.log('📊 RESUMEN DE DESCUENTOS APLICADOS:', resumen);
    return resumen;
  };

  // Efecto para detectar cambios en las dependencias que requieren recálculo de descuentos
  useEffect(() => {
    // Solo ejecutar si hay análisis agregados
    if (analisisAgregados.length > 0) {
      console.log('🔄 Detectado cambio en dependencias que afectan descuentos');
      
      // Si hay cambios en las dependencias críticas, recalcular los descuentos
      calcularDescuentoJerarquico();
    }
    
    // Dependencias que requieren recálculo de descuentos cuando cambian:
    // - Cliente (tercera/cuarta edad)
    // - Origen 
    // - Médico
    // - Medio de pago
    // - Perfil seleccionado
  }, [clienteSeleccionado, origen, medicoSeleccionado, medioPago, perfilSeleccionado]);

  // Retornar funciones y estados relevantes para usar en el componente principal
  return {
    calcularDescuentoJerarquico,
    mostrarResumenDescuentos,
    obtenerDescuentoPorAnalisis,
    obtenerDescuentoPorPerfil,
    recalcularTodosLosAnalisis,
    descuentosFuente,
    logDescuento,
    esTerceraEdad,
    esCuartaEdad
  };
};

export default useDescuentosJerarquicos;
